import { IStairCalculationResult, IStairConfig, IStepGeometry } from '@stair-craft/common/util';

import {
    EColorRef,
    EPrimitiveKind,
    ETextAlign,
    ETextBaseline,
    FONT_MONO_FAMILY,
    IFont,
    IPaneRect,
    IPlanPrimitive,
    IProjectionOutput,
    ITextPrimitive,
    emptyProjection,
} from './plan-primitive';

const PAD_LEFT: number = 12;
const PAD_RIGHT: number = 12;
const PAD_TOP: number = 26;
const PAD_BOTTOM: number = 18;
const MIN_INNER_W: number = 80;
const MIN_INNER_H: number = 60;
const CELL_MARGIN: number = 28;
const FIT_FACTOR: number = 0.85;
const SINGLE_ROW_MAX: number = 5;
const INNER_LABEL_MIN_PX: number = 18;

const NUMBER_FONT: IFont = { weight: 600, size: 11, family: FONT_MONO_FAMILY };
const DIM_FONT_TINY: IFont = { weight: 400, size: 8, family: FONT_MONO_FAMILY };

function text(
    at: [number, number],
    value: string,
    align: ETextAlign,
    baseline: ETextBaseline,
    color: EColorRef,
    font: IFont
): ITextPrimitive {
    return { kind: EPrimitiveKind.Text, text: value, at, align, baseline, color, font };
}

/**
 * Winder numbered templates (забежные ступени) — every winder tread as a numbered
 * wedge cell in a grid (≤5 in one row, otherwise two rows), each with its
 * inner-back→outer-front hypotenuse and width/inner-depth/outer-depth labels.
 * Pure projection in pane-local pixels. 1:1 port of
 * PlanViewComponent#drawWinderNumberedSteps.
 */
export function projectWinderNumbered(result: IStairCalculationResult, config: IStairConfig, pane: IPaneRect): IProjectionOutput {
    const winders: IStepGeometry[] = result.geometry.steps.filter((s: IStepGeometry) => s.isWinder);
    const count: number = winders.length;
    if (count === 0) {
        return emptyProjection();
    }

    const innerW: number = pane.w - PAD_LEFT - PAD_RIGHT;
    const innerH: number = pane.h - PAD_TOP - PAD_BOTTOM;
    if (innerW < MIN_INNER_W || innerH < MIN_INNER_H) {
        return emptyProjection();
    }

    const cols: number = count <= SINGLE_ROW_MAX ? count : Math.ceil(count / 2);
    const rows: number = Math.ceil(count / cols);
    const cellW: number = innerW / cols;
    const cellH: number = innerH / rows;

    // Reference dims from the first winder so every wedge shares a scale.
    const ref: IStepGeometry = winders[0];
    const refW: number = config.stairWidth;
    const refH: number = ref.outerDepth ?? ref.treadDepth;
    const scaleAll: number =
        Math.min((cellW - CELL_MARGIN) / Math.max(refW, refH), (cellH - CELL_MARGIN) / Math.max(refW, refH)) * FIT_FACTOR;

    const primitives: IPlanPrimitive[] = [];

    for (let i: number = 0; i < count; i++) {
        const step: IStepGeometry = winders[i];
        const innerDepth: number = step.innerDepth ?? step.treadDepth;
        const outerDepth: number = step.outerDepth ?? step.treadDepth;
        const width: number = config.stairWidth;
        const drawWidth: number = width * scaleAll;
        const drawOuter: number = outerDepth * scaleAll;
        const drawInner: number = innerDepth * scaleAll;

        const col: number = i % cols;
        const row: number = Math.floor(i / cols);
        const cellX: number = PAD_LEFT + col * cellW;
        const cellY: number = PAD_TOP + row * cellH;
        const ox: number = cellX + (cellW - drawWidth) / 2;
        const oy: number = cellY + (cellH - Math.max(drawOuter, drawWidth)) / 2;

        // Number label (top-left of the cell).
        primitives.push(text([cellX + 4, cellY + 2], String(i + 1), ETextAlign.Left, ETextBaseline.Top, EColorRef.Ink2, NUMBER_FONT));

        // Wedge — inner edge short (left), outer edge long (right).
        primitives.push({
            kind: EPrimitiveKind.Poly,
            points: [
                [ox, oy],
                [ox + drawWidth, oy],
                [ox + drawWidth, oy + drawOuter],
                [ox, oy + drawInner],
            ],
            closed: true,
            fill: EColorRef.Winder,
            stroke: EColorRef.StepStroke,
            width: 1.2,
        });

        // Diagonal hypotenuse (inner-back → outer-front).
        primitives.push({
            kind: EPrimitiveKind.Poly,
            points: [
                [ox, oy],
                [ox + drawWidth, oy + drawOuter],
            ],
            closed: false,
            stroke: EColorRef.Ink3,
            width: 0.8,
            dash: [3, 2],
        });

        // Mini dimension labels.
        primitives.push(
            text(
                [ox + drawWidth / 2, oy - 4],
                String(Math.round(width)),
                ETextAlign.Center,
                ETextBaseline.Bottom,
                EColorRef.Ink3,
                DIM_FONT_TINY
            )
        );
        primitives.push(
            text(
                [ox + drawWidth + 4, oy + drawOuter / 2],
                String(Math.round(outerDepth)),
                ETextAlign.Left,
                ETextBaseline.Middle,
                EColorRef.Ink3,
                DIM_FONT_TINY
            )
        );
        if (drawInner > INNER_LABEL_MIN_PX) {
            primitives.push(
                text(
                    [ox - 4, oy + drawInner / 2],
                    String(Math.round(innerDepth)),
                    ETextAlign.Right,
                    ETextBaseline.Middle,
                    EColorRef.Ink3,
                    DIM_FONT_TINY
                )
            );
        }
    }

    // Every wedge shares one scale, so the whole grid converts with it; the cells are laid
    // out in pixels, so the millimetre origin is the pane's own corner.
    return { primitives, pxPerMm: scaleAll, origin: { x: 0, y: 0 } };
}
